import React, { useEffect, useState } from 'react';
import axios from 'axios';
import Header from '../components/Header';
import Navigation from '../components/Navigation';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Checkbox } from '../components/ui/checkbox';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { toast } from 'sonner';
import { Clock, FileText, CheckCircle, XCircle, Users, Loader2, Timer } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MyApprovalsPage = ({ user, setUser }) => {
  const [pendingAttendance, setPendingAttendance] = useState([]);
  const [pendingLeaves, setPendingLeaves] = useState([]);
  const [pendingOvertime, setPendingOvertime] = useState([]);
  const [directReportsCount, setDirectReportsCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [processingId, setProcessingId] = useState(null);

  // Bulk-selection state — one Set per tab, plus a single processing flag.
  const [selAtt, setSelAtt] = useState(() => new Set());
  const [selLeave, setSelLeave] = useState(() => new Set());
  const [selOt, setSelOt] = useState(() => new Set());
  const [bulkBusy, setBulkBusy] = useState(false);
  // { kind, status, ids } — non-null shows the confirm dialog.
  const [pendingBulk, setPendingBulk] = useState(null);
  // Reject-with-reason dialog state.
  // Shape: { kind: 'attendance'|'leaves'|'overtime', id, isBulk: bool, ids?: [] }
  const [rejectIntent, setRejectIntent] = useState(null);
  const [rejectReason, setRejectReason] = useState('');

  useEffect(() => {
    fetchPendingApprovals();
  }, []);

  const fetchPendingApprovals = async () => {
    try {
      const token = localStorage.getItem('token');
      const config = { headers: { Authorization: `Bearer ${token}` } };
      
      const response = await axios.get(`${API}/my-approvals/pending`, config);
      
      setPendingAttendance(response.data.attendance || []);
      setPendingLeaves(response.data.leaves || []);
      setPendingOvertime(response.data.overtime || []);
      setDirectReportsCount(response.data.direct_reports_count || 0);
    } catch (error) {
      console.error('Error fetching approvals:', error);
      toast.error('Failed to load pending approvals');
    } finally {
      setLoading(false);
    }
  };

  const handleAttendanceApproval = async (attendanceId, status, rejection_reason = null) => {
    setProcessingId(attendanceId);
    try {
      const token = localStorage.getItem('token');
      const config = { headers: { Authorization: `Bearer ${token}` } };
      
      await axios.post(`${API}/my-approvals/attendance/approve`, {
        attendance_id: attendanceId,
        approval_status: status,
        rejection_reason
      }, config);
      
      toast.success(`Attendance ${status === 'approved' ? 'approved' : 'rejected'} successfully`);
      fetchPendingApprovals();
    } catch (error) {
      console.error('Error processing approval:', error);
      toast.error(error.response?.data?.detail || 'Failed to process approval');
    } finally {
      setProcessingId(null);
    }
  };

  const handleLeaveApproval = async (leaveId, status, rejection_reason = null) => {
    setProcessingId(leaveId);
    try {
      const token = localStorage.getItem('token');
      const config = { headers: { Authorization: `Bearer ${token}` } };
      
      await axios.post(`${API}/my-approvals/leave/approve`, {
        leave_id: leaveId,
        approval_status: status,
        rejection_reason
      }, config);
      
      toast.success(`Leave ${status === 'approved' ? 'approved' : 'rejected'} successfully`);
      fetchPendingApprovals();
    } catch (error) {
      console.error('Error processing approval:', error);
      toast.error(error.response?.data?.detail || 'Failed to process approval');
    } finally {
      setProcessingId(null);
    }
  };

  const handleOvertimeApproval = async (overtimeId, status, rejection_reason = null) => {
    setProcessingId(overtimeId);
    try {
      const token = localStorage.getItem('token');
      const config = { headers: { Authorization: `Bearer ${token}` } };
      
      await axios.post(`${API}/my-approvals/overtime/approve`, {
        overtime_id: overtimeId,
        approval_status: status,
        rejection_reason
      }, config);
      
      toast.success(`Overtime ${status === 'approved' ? 'approved' : 'rejected'} successfully`);
      fetchPendingApprovals();
    } catch (error) {
      console.error('Error processing approval:', error);
      toast.error(error.response?.data?.detail || 'Failed to process approval');
    } finally {
      setProcessingId(null);
    }
  };

  // ----- bulk approve / reject ----------------------------------------------
  const KIND_PATH = { attendance: 'attendance', leaves: 'leave', overtime: 'overtime' };

  const toggleOne = (kind, id, checked) => {
    const setters = { attendance: setSelAtt, leaves: setSelLeave, overtime: setSelOt };
    setters[kind]((prev) => {
      const next = new Set(prev);
      if (checked) next.add(id); else next.delete(id);
      return next;
    });
  };

  const toggleAll = (kind, rows, checked) => {
    const setters = { attendance: setSelAtt, leaves: setSelLeave, overtime: setSelOt };
    setters[kind](checked ? new Set(rows.map(r => r.id)) : new Set());
  };

  const clearSelections = () => {
    setSelAtt(new Set());
    setSelLeave(new Set());
    setSelOt(new Set());
  };

  const handleBulkAction = (kind, status) => {
    const sel = kind === 'attendance' ? selAtt : kind === 'leaves' ? selLeave : selOt;
    const ids = Array.from(sel);
    if (ids.length === 0) return;
    if (status === 'rejected') {
      // Route through the reason dialog so the manager can explain why.
      openRejectDialog({ kind, ids });
      return;
    }
    // Approve: keep the existing confirm dialog
    setPendingBulk({ kind, status, ids });
  };

  const executeBulkAction = async () => {
    if (!pendingBulk) return;
    const { kind, status, ids, rejection_reason } = pendingBulk;
    const verb = status === 'approved' ? 'Approve' : 'Reject';
    setBulkBusy(true);
    try {
      const token = localStorage.getItem('token');
      const config = { headers: { Authorization: `Bearer ${token}` } };
      const res = await axios.post(
        `${API}/my-approvals/${KIND_PATH[kind]}/bulk-approve`,
        { ids, approval_status: status, rejection_reason: rejection_reason || null },
        config,
      );
      const { succeeded, failed } = res.data;
      if (failed === 0) {
        toast.success(`${verb}d ${succeeded} ${kind} request${succeeded > 1 ? 's' : ''}`);
      } else {
        toast.warning(`${verb}d ${succeeded}, ${failed} failed — check details`);
      }
      clearSelections();
      fetchPendingApprovals();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Bulk action failed');
    } finally {
      setBulkBusy(false);
      setPendingBulk(null);
    }
  };

  // Open the reject-with-reason dialog (works for single & bulk).
  // kind: 'attendance' | 'leaves' | 'overtime', id: singular row id, ids?: bulk
  const openRejectDialog = ({ kind, id, ids }) => {
    setRejectReason('');
    setRejectIntent({
      kind,
      id: id || null,
      ids: ids || null,
      isBulk: Array.isArray(ids) && ids.length > 0,
    });
  };

  const executeReject = async () => {
    if (!rejectIntent) return;
    const reason = (rejectReason || '').trim() || null;
    const { kind, id, isBulk, ids } = rejectIntent;
    setRejectIntent(null); // close dialog immediately for snappier UX
    if (isBulk) {
      // hand-off to the bulk pipeline so we reuse its toasts + state cleanup
      setPendingBulk({ kind, status: 'rejected', ids, rejection_reason: reason });
      // we open AlertDialog automatically via pendingBulk truthiness; user already
      // confirmed reason — auto-execute by short-circuiting executeBulkAction.
      // The simplest path is to call executeBulkAction directly with the data:
      try {
        const token = localStorage.getItem('token');
        const config = { headers: { Authorization: `Bearer ${token}` } };
        setBulkBusy(true);
        const res = await axios.post(
          `${API}/my-approvals/${KIND_PATH[kind]}/bulk-approve`,
          { ids, approval_status: 'rejected', rejection_reason: reason },
          config,
        );
        const { succeeded, failed } = res.data;
        if (failed === 0) toast.success(`Rejected ${succeeded} ${kind} request${succeeded > 1 ? 's' : ''}`);
        else toast.warning(`Rejected ${succeeded}, ${failed} failed`);
        clearSelections();
        fetchPendingApprovals();
      } catch (e) {
        toast.error(e.response?.data?.detail || 'Bulk reject failed');
      } finally {
        setBulkBusy(false);
        setPendingBulk(null);
      }
      return;
    }
    // singular reject
    if (kind === 'attendance') return handleAttendanceApproval(id, 'rejected', reason);
    if (kind === 'leaves') return handleLeaveApproval(id, 'rejected', reason);
    if (kind === 'overtime') return handleOvertimeApproval(id, 'rejected', reason);
  };

  const renderBulkBar = (kind, rows) => {
    const sel = kind === 'attendance' ? selAtt : kind === 'leaves' ? selLeave : selOt;
    if (sel.size === 0) return null;
    return (
      <div
        className="flex items-center justify-between gap-3 px-4 py-3 bg-indigo-50 border-y border-indigo-200"
        data-testid={`bulk-bar-${kind}`}
      >
        <div className="text-sm text-indigo-900">
          <strong data-testid={`bulk-count-${kind}`}>{sel.size}</strong> of {rows.length} selected
        </div>
        <div className="flex items-center gap-2">
          <Button
            size="sm"
            variant="outline"
            className="text-green-700 border-green-600 hover:bg-green-50"
            onClick={() => handleBulkAction(kind, 'approved')}
            disabled={bulkBusy}
            data-testid={`bulk-approve-${kind}`}
          >
            {bulkBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle className="w-4 h-4 mr-1" />}
            Approve selected
          </Button>
          <Button
            size="sm"
            variant="outline"
            className="text-red-700 border-red-600 hover:bg-red-50"
            onClick={() => handleBulkAction(kind, 'rejected')}
            disabled={bulkBusy}
            data-testid={`bulk-reject-${kind}`}
          >
            {bulkBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <XCircle className="w-4 h-4 mr-1" />}
            Reject selected
          </Button>
          <Button
            size="sm"
            variant="ghost"
            onClick={() => toggleAll(kind, rows, false)}
            disabled={bulkBusy}
          >
            Clear
          </Button>
        </div>
      </div>
    );
  };


  const getStatusLabel = (status) => {
    const statusMap = {
      'P': 'Present',
      'UP': 'Unpaid Leave',
      'AL': 'Annual Leave',
      'AHD': 'Annual Half Day',
      'UPHD': 'Unpaid Half Day',
      'OH': 'Organizational Holiday',
      'RH': 'Restricted Holiday',
      'WO': 'Week Off'
    };
    return statusMap[status] || status;
  };

  const getLeaveTypeLabel = (type) => {
    const typeMap = {
      'AL': 'Annual Leave',
      'OH': 'Organizational Holiday',
      'RH': 'Restricted Holiday',
      'LWP': 'Leave Without Pay'
    };
    return typeMap[type] || type;
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
        <Header user={user} setUser={setUser} />
        <Navigation user={user} />
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
          <span className="ml-2 text-gray-600">Loading approvals...</span>
        </div>
      </div>
    );
  }

  const totalPending = pendingAttendance.length + pendingLeaves.length + pendingOvertime.length;

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
      <Header user={user} setUser={setUser} />
      <Navigation user={user} />
      
      <main className="p-6">
        <div className="max-w-screen-2xl mx-auto">
          {/* Summary Cards */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
            <Card className="bg-white shadow-sm">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-500">Direct Reports</p>
                    <p className="text-2xl font-bold text-blue-600">{directReportsCount}</p>
                  </div>
                  <Users className="w-10 h-10 text-blue-200" />
                </div>
              </CardContent>
            </Card>
            
            <Card className="bg-white shadow-sm">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-500">Pending Attendance</p>
                    <p className="text-2xl font-bold text-orange-600">{pendingAttendance.length}</p>
                  </div>
                  <Clock className="w-10 h-10 text-orange-200" />
                </div>
              </CardContent>
            </Card>
            
            <Card className="bg-white shadow-sm">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-500">Pending Leaves</p>
                    <p className="text-2xl font-bold text-purple-600">{pendingLeaves.length}</p>
                  </div>
                  <FileText className="w-10 h-10 text-purple-200" />
                </div>
              </CardContent>
            </Card>
            
            <Card className="bg-white shadow-sm">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-500">Pending Overtime</p>
                    <p className="text-2xl font-bold text-teal-600">{pendingOvertime.length}</p>
                  </div>
                  <Timer className="w-10 h-10 text-teal-200" />
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Main Content */}
          <Card className="bg-white shadow-lg">
            <CardHeader className="border-b">
              <CardTitle className="flex items-center gap-2">
                <CheckCircle className="w-5 h-5 text-green-600" />
                Pending Approvals
                {totalPending > 0 && (
                  <Badge variant="destructive" className="ml-2">{totalPending}</Badge>
                )}
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <Tabs defaultValue="attendance" className="w-full">
                <TabsList className="w-full justify-start rounded-none border-b bg-gray-50 p-0">
                  <TabsTrigger 
                    value="attendance" 
                    className="rounded-none border-b-2 border-transparent data-[state=active]:border-blue-600 data-[state=active]:bg-white px-6 py-3"
                  >
                    <Clock className="w-4 h-4 mr-2" />
                    Attendance ({pendingAttendance.length})
                  </TabsTrigger>
                  <TabsTrigger 
                    value="leaves"
                    className="rounded-none border-b-2 border-transparent data-[state=active]:border-blue-600 data-[state=active]:bg-white px-6 py-3"
                  >
                    <FileText className="w-4 h-4 mr-2" />
                    Leave Requests ({pendingLeaves.length})
                  </TabsTrigger>
                  <TabsTrigger 
                    value="overtime"
                    className="rounded-none border-b-2 border-transparent data-[state=active]:border-blue-600 data-[state=active]:bg-white px-6 py-3"
                  >
                    <Timer className="w-4 h-4 mr-2" />
                    Overtime ({pendingOvertime.length})
                  </TabsTrigger>
                </TabsList>

                {/* Attendance Tab */}
                <TabsContent value="attendance" className="mt-0">
                  {pendingAttendance.length === 0 ? (
                    <div className="text-center py-12 text-gray-500">
                      <Clock className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                      <p>No pending attendance approvals</p>
                    </div>
                  ) : (
                    <>
                      {renderBulkBar("attendance", pendingAttendance)}
                      <Table>
                        <TableHeader>
                          <TableRow className="bg-gray-50">
                            <TableHead className="w-10">
                              <Checkbox
                                checked={pendingAttendance.length > 0 && selAtt.size === pendingAttendance.length}
                                onCheckedChange={(c) => toggleAll('attendance', pendingAttendance, !!c)}
                                aria-label="Select all attendance"
                                data-testid="select-all-attendance"
                              />
                            </TableHead>
                            <TableHead>Employee</TableHead>
                            <TableHead>Employee ID</TableHead>
                            <TableHead>Date</TableHead>
                            <TableHead>Status</TableHead>
                            <TableHead>Submitted</TableHead>
                            <TableHead className="text-center">Actions</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {pendingAttendance.map((record) => (
                            <TableRow key={record.id} className="hover:bg-gray-50" data-state={selAtt.has(record.id) ? 'selected' : undefined}>
                              <TableCell>
                                <Checkbox
                                  checked={selAtt.has(record.id)}
                                  onCheckedChange={(c) => toggleOne('attendance', record.id, !!c)}
                                  aria-label="Select row"
                                  data-testid={`select-att-${record.id}`}
                                />
                              </TableCell>
                              <TableCell className="font-medium">{record.employee_name}</TableCell>
                            <TableCell>
                              <Badge variant="outline">{record.employee_code}</Badge>
                            </TableCell>
                            <TableCell>{formatDate(record.date)}</TableCell>
                            <TableCell>
                              <Badge 
                                variant={record.status === 'P' ? 'default' : 'secondary'}
                                className={record.status === 'P' ? 'bg-green-100 text-green-800' : ''}
                              >
                                {getStatusLabel(record.status)}
                              </Badge>
                            </TableCell>
                            <TableCell className="text-gray-500 text-sm">
                              {formatDate(record.submitted_at)}
                            </TableCell>
                            <TableCell>
                              <div className="flex items-center justify-center gap-2">
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="text-green-600 border-green-600 hover:bg-green-50"
                                  onClick={() => handleAttendanceApproval(record.id, 'approved')}
                                  disabled={processingId === record.id}
                                >
                                  {processingId === record.id ? (
                                    <Loader2 className="w-4 h-4 animate-spin" />
                                  ) : (
                                    <>
                                      <CheckCircle className="w-4 h-4 mr-1" />
                                      Approve
                                    </>
                                  )}
                                </Button>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="text-red-600 border-red-600 hover:bg-red-50"
                                  onClick={() => openRejectDialog({ kind: 'attendance', id: record.id })}
                                  disabled={processingId === record.id}
                                >
                                  {processingId === record.id ? (
                                    <Loader2 className="w-4 h-4 animate-spin" />
                                  ) : (
                                    <>
                                      <XCircle className="w-4 h-4 mr-1" />
                                      Reject
                                    </>
                                  )}
                                </Button>
                              </div>
                            </TableCell>
                          </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </>
                  )}
                </TabsContent>

                {/* Leaves Tab */}
                <TabsContent value="leaves" className="mt-0">
                  {pendingLeaves.length === 0 ? (
                    <div className="text-center py-12 text-gray-500">
                      <FileText className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                      <p>No pending leave approvals</p>
                    </div>
                  ) : (
                    <>
                      {renderBulkBar("leaves", pendingLeaves)}
                      <Table>
                        <TableHeader>
                          <TableRow className="bg-gray-50">
                            <TableHead className="w-10">
                              <Checkbox
                                checked={pendingLeaves.length > 0 && selLeave.size === pendingLeaves.length}
                                onCheckedChange={(c) => toggleAll('leaves', pendingLeaves, !!c)}
                                aria-label="Select all leaves"
                                data-testid="select-all-leaves"
                              />
                            </TableHead>
                            <TableHead>Employee</TableHead>
                            <TableHead>Employee ID</TableHead>
                            <TableHead>Leave Type</TableHead>
                            <TableHead>From</TableHead>
                            <TableHead>To</TableHead>
                            <TableHead>Reason</TableHead>
                            <TableHead className="text-center">Actions</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {pendingLeaves.map((leave) => (
                            <TableRow key={leave.id} className="hover:bg-gray-50" data-state={selLeave.has(leave.id) ? 'selected' : undefined}>
                              <TableCell>
                                <Checkbox
                                  checked={selLeave.has(leave.id)}
                                  onCheckedChange={(c) => toggleOne('leaves', leave.id, !!c)}
                                  aria-label="Select row"
                                  data-testid={`select-leave-${leave.id}`}
                                />
                              </TableCell>
                              <TableCell className="font-medium">{leave.employee_name}</TableCell>
                            <TableCell>
                              <Badge variant="outline">{leave.employee_code}</Badge>
                            </TableCell>
                            <TableCell>
                              <Badge variant="secondary">
                                {getLeaveTypeLabel(leave.leave_type)}
                              </Badge>
                              {leave.leave_category === 'half_day' && (
                                <Badge variant="outline" className="ml-1 text-xs">Half Day</Badge>
                              )}
                            </TableCell>
                            <TableCell>{formatDate(leave.from_date)}</TableCell>
                            <TableCell>{formatDate(leave.to_date)}</TableCell>
                            <TableCell className="max-w-[200px] truncate" title={leave.reason}>
                              {leave.reason}
                            </TableCell>
                            <TableCell>
                              <div className="flex items-center justify-center gap-2">
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="text-green-600 border-green-600 hover:bg-green-50"
                                  onClick={() => handleLeaveApproval(leave.id, 'approved')}
                                  disabled={processingId === leave.id}
                                >
                                  {processingId === leave.id ? (
                                    <Loader2 className="w-4 h-4 animate-spin" />
                                  ) : (
                                    <>
                                      <CheckCircle className="w-4 h-4 mr-1" />
                                      Approve
                                    </>
                                  )}
                                </Button>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="text-red-600 border-red-600 hover:bg-red-50"
                                  onClick={() => openRejectDialog({ kind: 'leaves', id: leave.id })}
                                  disabled={processingId === leave.id}
                                >
                                  {processingId === leave.id ? (
                                    <Loader2 className="w-4 h-4 animate-spin" />
                                  ) : (
                                    <>
                                      <XCircle className="w-4 h-4 mr-1" />
                                      Reject
                                    </>
                                  )}
                                </Button>
                              </div>
                            </TableCell>
                          </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </>
                  )}
                </TabsContent>

                {/* Overtime Tab */}
                <TabsContent value="overtime" className="mt-0">
                  {pendingOvertime.length === 0 ? (
                    <div className="text-center py-12 text-gray-500">
                      <Timer className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                      <p>No pending overtime approvals</p>
                    </div>
                  ) : (
                    <>
                      {renderBulkBar("overtime", pendingOvertime)}
                      <Table>
                        <TableHeader>
                          <TableRow className="bg-gray-50">
                            <TableHead className="w-10">
                              <Checkbox
                                checked={pendingOvertime.length > 0 && selOt.size === pendingOvertime.length}
                                onCheckedChange={(c) => toggleAll('overtime', pendingOvertime, !!c)}
                                aria-label="Select all overtime"
                                data-testid="select-all-overtime"
                              />
                            </TableHead>
                            <TableHead>Employee</TableHead>
                            <TableHead>Employee ID</TableHead>
                            <TableHead>Start</TableHead>
                            <TableHead>End</TableHead>
                            <TableHead>Total Overtime</TableHead>
                            <TableHead>Reason</TableHead>
                            <TableHead className="text-center">Actions</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {pendingOvertime.map((overtime) => (
                            <TableRow key={overtime.id} className="hover:bg-gray-50" data-state={selOt.has(overtime.id) ? 'selected' : undefined}>
                              <TableCell>
                                <Checkbox
                                  checked={selOt.has(overtime.id)}
                                  onCheckedChange={(c) => toggleOne('overtime', overtime.id, !!c)}
                                  aria-label="Select row"
                                  data-testid={`select-ot-${overtime.id}`}
                                />
                              </TableCell>
                              <TableCell className="font-medium">{overtime.employee_name}</TableCell>
                            <TableCell>
                              <Badge variant="outline">{overtime.employee_code}</Badge>
                            </TableCell>
                            <TableCell>
                              <div className="text-sm">{formatDate(overtime.start_date)}</div>
                              <div className="text-xs text-gray-500">{overtime.start_time}</div>
                            </TableCell>
                            <TableCell>
                              <div className="text-sm">{formatDate(overtime.end_date)}</div>
                              <div className="text-xs text-gray-500">{overtime.end_time}</div>
                            </TableCell>
                            <TableCell>
                              <span className="font-bold text-teal-600 text-lg">{overtime.total_overtime}</span>
                            </TableCell>
                            <TableCell className="max-w-[150px] truncate" title={overtime.reason}>
                              {overtime.reason || '-'}
                            </TableCell>
                            <TableCell>
                              <div className="flex items-center justify-center gap-2">
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="text-green-600 border-green-600 hover:bg-green-50"
                                  onClick={() => handleOvertimeApproval(overtime.id, 'approved')}
                                  disabled={processingId === overtime.id}
                                >
                                  {processingId === overtime.id ? (
                                    <Loader2 className="w-4 h-4 animate-spin" />
                                  ) : (
                                    <>
                                      <CheckCircle className="w-4 h-4 mr-1" />
                                      Approve
                                    </>
                                  )}
                                </Button>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="text-red-600 border-red-600 hover:bg-red-50"
                                  onClick={() => openRejectDialog({ kind: 'overtime', id: overtime.id })}
                                  disabled={processingId === overtime.id}
                                >
                                  {processingId === overtime.id ? (
                                    <Loader2 className="w-4 h-4 animate-spin" />
                                  ) : (
                                    <>
                                      <XCircle className="w-4 h-4 mr-1" />
                                      Reject
                                    </>
                                  )}
                                </Button>
                              </div>
                            </TableCell>
                          </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </>
                  )}
                </TabsContent>
              </Tabs>
            </CardContent>
          </Card>
        </div>
      </main>

      <AlertDialog
        open={!!pendingBulk}
        onOpenChange={(open) => { if (!open && !bulkBusy) setPendingBulk(null); }}
      >
        <AlertDialogContent data-testid="bulk-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>
              {pendingBulk?.status === 'approved' ? 'Approve' : 'Reject'} {pendingBulk?.ids?.length || 0}{' '}
              {pendingBulk?.kind} request{(pendingBulk?.ids?.length || 0) > 1 ? 's' : ''}?
            </AlertDialogTitle>
            <AlertDialogDescription>
              This action will{' '}
              <strong>{pendingBulk?.status === 'approved' ? 'approve' : 'reject'}</strong>{' '}
              every selected {pendingBulk?.kind} request and notify the employees by email.
              You cannot undo this from this screen.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={bulkBusy} data-testid="bulk-cancel-btn">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={executeBulkAction}
              disabled={bulkBusy}
              className={pendingBulk?.status === 'approved'
                ? 'bg-green-600 hover:bg-green-700'
                : 'bg-red-600 hover:bg-red-700'}
              data-testid="bulk-confirm-btn"
            >
              {bulkBusy
                ? 'Processing…'
                : (pendingBulk?.status === 'approved' ? 'Yes, approve all' : 'Yes, reject all')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Reject-with-reason dialog (used for both singular & bulk rejects) */}
      <AlertDialog
        open={!!rejectIntent}
        onOpenChange={(open) => { if (!open) { setRejectIntent(null); setRejectReason(''); } }}
      >
        <AlertDialogContent data-testid="reject-reason-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>
              Reject {rejectIntent?.isBulk
                ? `${rejectIntent?.ids?.length || 0} ${rejectIntent?.kind} request${(rejectIntent?.ids?.length || 0) > 1 ? 's' : ''}`
                : `this ${rejectIntent?.kind === 'leaves' ? 'leave' : rejectIntent?.kind}`}?
            </AlertDialogTitle>
            <AlertDialogDescription>
              The employee will see this reason next to the &quot;Resubmit&quot; button,
              so be specific — e.g. &quot;Wrong date — please pick yesterday&quot;.
              Leave it blank if you don&apos;t want to add context.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="space-y-2 my-2">
            <Label htmlFor="reject-reason-input" className="text-sm">Rejection reason (optional)</Label>
            <Textarea
              id="reject-reason-input"
              rows={3}
              maxLength={500}
              placeholder="Why are you rejecting?"
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              data-testid="reject-reason-input"
            />
            <div className="text-xs text-gray-500">{rejectReason.length}/500</div>
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="reject-cancel-btn">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={executeReject}
              className="bg-red-600 hover:bg-red-700"
              data-testid="reject-confirm-btn"
            >
              Reject
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default MyApprovalsPage;
